import React, { useMemo, FC, useEffect, useState } from "react";
import Fuse from "fuse.js";
import * as Yup from "yup";
import classNames from "classnames";
import FormLabel from "../FormLabel";
import { AddButton, PrimaryButton, SecondaryButton } from "../Buttons";
import { Formik, FormikProps } from "formik";
import { useAppSelector } from "../../store/hooks";
import { FormTextInput, FormSearch } from "../FormFields";
import { formatFloat } from "../../utils";
import {
  PieceInterface,
  ShipmentService,
  PieceCategoryInterface,
} from "../../interfaces";

interface PieceFormValues {
  [key: string]: string | number;
}
interface FormInterface {
  formik: FormikProps<PieceFormValues>;
  type: ShipmentService;
  pieceCategories: PieceCategoryInterface[];
  pieces?: PieceInterface[];
  editMode?: boolean;
  maxSumWeight: number;
  autoCompleteOff?: boolean;
  minSumWeight?: number;
  cleaned?: boolean;
  showDimentionsHelp?: boolean;
  weightWarn: boolean;
  chargedWeight?: number;
  handleNumberChange: (
    event: React.ChangeEvent<HTMLInputElement>,
    formik: FormikProps<any>,
    allowFloats?: boolean
  ) => void;
  setEditMode?: (value: boolean) => void;
  setCleaned?: (value: boolean) => void;
  setOpenDimentionsHelp?: (open: boolean) => void;
}
const DocumentTypeName = "Correspondencias";

const Form: FC<FormInterface> = ({
  formik,
  type,
  pieceCategories,
  pieces,
  editMode,
  maxSumWeight,
  autoCompleteOff,
  minSumWeight,
  cleaned,
  chargedWeight,
  setCleaned,
  weightWarn,
  handleNumberChange,
  setEditMode = () => {},
}) => {
  const categories = useAppSelector((state) => state.inmutable.pieceCategories);

  const [search, setSearch] = useState<string>("");
  const fuse = useMemo(
    () =>
      new Fuse(categories, {
        keys: ["descriptionTags", "name"],
        threshold: 0.2,
        ignoreLocation: true,
      }),
    [categories]
  );
  const filteredCategories = fuse.search(search, { limit: 5 });

  useEffect(() => {
    if (
      type === ShipmentService.DOCUMENT ||
      type === ShipmentService.DOCUMENT_INTERNATIONAL
    ) {
      // Set category to Documents
      formik.setFieldValue(
        "category",
        pieceCategories.find((d) => d.name === DocumentTypeName)?.name
      );

      // Set dimentions
      formik.setFieldValue("height", 30);
      formik.setFieldValue("width", 23);
      formik.setFieldValue("length", 1);
      formik.setFieldValue("amount", 1);
    } else if (!formik.values.category) {
      setSearch("");
      formik.setFieldValue("height", "");
      formik.setFieldValue("width", "");
      formik.setFieldValue("length", "");
      formik.setFieldValue("amount", 1);
    }

    // Only the type is needed to change the category to "Documentos"
    // if the type is "document" or to deselect it if the type it is "regular"
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, formik.values.category]);

  useEffect(() => {
    if (cleaned) {
      formik.resetForm();
      setCleaned?.(false);
    }
  }, [cleaned]);

  return (
    <form onSubmit={formik.handleSubmit} className="flex h-full flex-col gap-6">
      {/* Header */}
      <div className="flex items-start">
        <p className="font-semibold leading-6 text-gray-600">
          Complete los datos de la pieza:
        </p>
      </div>

      {/* Body */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-1 flex-col">
          <div className="relative w-full">
            <FormSearch
              value={search}
              label="Categorías"
              name="category"
              placeholder="Buscar categoría..."
              disabled={
                type === ShipmentService.DOCUMENT ||
                type === ShipmentService.DOCUMENT_INTERNATIONAL
              }
              onChange={(e) => setSearch(e.target.value)}
              options={
                search.length > 0
                  ? filteredCategories.map((r) => r.item.name).length < 1 &&
                    search.length > 0
                    ? ["Otras Categorías"]
                    : filteredCategories.map((r) => r.item.name)
                  : categories.map((r) => r.name)
              }
              onSelectOption={async (category) => {
                setSearch(category);
                formik.setFieldValue("category", category);
              }}
              RenderOption={({ option }) => <p> {option} </p>}
              onChangeFocus={(focus) => {
                if (!focus && formik.values.category)
                  setSearch(formik.values.category as string);
              }}
              error={
                formik.touched.category && formik.errors.category
                  ? formik.errors.category
                  : undefined
              }
            />
          </div>
        </div>

        <div>
          <FormTextInput
            name="weight"
            label="Peso en kg."
            value={formik.values.weight}
            disabled={!formik.values.category}
            error={
              formik.touched.weight && formik.errors.weight
                ? formik.errors.weight
                : undefined
            }
            onChange={(e) => handleNumberChange(e, formik, true)}
            onBlur={formik.handleBlur}
            autoComplete={autoCompleteOff ? "off" : "on"}
          />
        </div>

        <div
          className={classNames(type === ShipmentService.DOCUMENT && "hidden")}
        >
          <div className="flex items-center gap-2">
            <div>
              <FormLabel fieldName="Dimensiones en cm." />
            </div>

            {/* <LinkText
              text="Cómo llenar"
              className={classNames(
                "text-xs mb-2",
                !showDimentionsHelp && "hidden"
              )}
              onClick={() => setOpenDimentionsHelp?.(true)}
            /> */}
          </div>

          <div className="flex flex-1 gap-4 flex-row">
            <div className="flex flex-1 flex-col">
              <FormTextInput
                name="height"
                type="number"
                label="Alto"
                value={formik.values.height}
                disabled={
                  !formik.values.category ||
                  type === ShipmentService.DOCUMENT ||
                  type === ShipmentService.DOCUMENT_INTERNATIONAL
                }
                error={
                  formik.touched.height && formik.errors.height
                    ? formik.errors.height
                    : undefined
                }
                onChange={(e) => handleNumberChange(e, formik, false)}
                onBlur={formik.handleBlur}
                autoComplete={autoCompleteOff ? "off" : "on"}
              />
            </div>

            <div className="flex flex-1 flex-col">
              <FormTextInput
                name="width"
                type="number"
                label="Ancho"
                value={formik.values.width}
                disabled={
                  !formik.values.category ||
                  type === ShipmentService.DOCUMENT ||
                  type === ShipmentService.DOCUMENT_INTERNATIONAL
                }
                error={
                  formik.touched.width && formik.errors.width
                    ? formik.errors.width
                    : undefined
                }
                onChange={(e) => handleNumberChange(e, formik, false)}
                onBlur={formik.handleBlur}
                autoComplete={autoCompleteOff ? "off" : "on"}
              />
            </div>

            <div className="flex flex-1 flex-col">
              <FormTextInput
                name="length"
                type="number"
                label="Largo"
                value={formik.values.length}
                disabled={
                  !formik.values.category ||
                  type === ShipmentService.DOCUMENT ||
                  type === ShipmentService.DOCUMENT_INTERNATIONAL
                }
                error={
                  formik.touched.length && formik.errors.length
                    ? formik.errors.length
                    : undefined
                }
                onChange={(e) => handleNumberChange(e, formik, false)}
                onBlur={formik.handleBlur}
                autoComplete={autoCompleteOff ? "off" : "on"}
              />
            </div>
          </div>
          {(formik.errors.height === " " ||
            formik.errors.width === " " ||
            formik.errors.length === " ") && (
            <p className="text-red-500 text-sm">
              Las medidas son inválidas, por favor revíselas y corríjalas
            </p>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="flex justify-between sm:items-end w-full gap-4 flex-col sm:flex-row">
        <div>
          <FormTextInput
            name="amount"
            type="number"
            label="Cantidad"
            value={formik.values.amount}
            disabled={
              !formik.values.category ||
              type === ShipmentService.DOCUMENT ||
              type === ShipmentService.DOCUMENT_INTERNATIONAL ||
              editMode
            }
            error={
              formik.touched.amount && formik.errors.amount
                ? formik.errors.amount
                : undefined
            }
            onChange={(e) => handleNumberChange(e, formik, false)}
            onBlur={formik.handleBlur}
            autoComplete={autoCompleteOff ? "off" : "on"}
          />
        </div>

        <div className={classNames(editMode && "hidden")}>
          <AddButton
            type="submit"
            disabled={
              !formik.isValid ||
              pieces!.reduce((r, p) => r + p.weight, 0) >= maxSumWeight ||
              !Object.values(formik.values).some((value) => !!value) ||
              ((type === ShipmentService.DOCUMENT ||
                type === ShipmentService.DOCUMENT_INTERNATIONAL) &&
                pieces &&
                pieces?.length > 0)
            }
          />
        </div>

        <SecondaryButton
          type="button"
          className={classNames(!editMode && "hidden")}
          onClick={() => setEditMode(false)}
        >
          Cancelar
        </SecondaryButton>

        <PrimaryButton
          type="submit"
          className={classNames(!editMode && "hidden")}
        >
          Guardar
        </PrimaryButton>
      </div>

      <div
        className={classNames(
          "flex-1 w-full rounded-lg bg-yellow-100 border-yellow-400 text-yellow-700 p-2 text-sm border",
          !weightWarn && "hidden"
        )}
      >
        <p className="font-semibold">Advertencia</p>
        <p className="text-xs">
          Los correos internos no deben superar los{" "}
          <span className="font-semibold">6 kg</span> excepto casos autorizados.
          Asegúrese de contar con aprobación del equipo SAC; estos casos
          serán revisados.
        </p>
      </div>

      <div
        className={classNames(
          "flex-1 w-full rounded-lg bg-red-100 border-red-400 text-red-700 p-2 text-sm border",
          (!chargedWeight || chargedWeight <= maxSumWeight) && "hidden"
        )}
      >
        {/* <p className="font-semibold">Advertencia</p> */}
        <p className="text-xs">
          El peso a facturar (
          <span className="font-semibold">{chargedWeight?.toFixed(2)}kg.</span>){" "}
          es mayor que el máximo permitido (
          <span className="font-semibold">{maxSumWeight.toFixed(2)}kg.</span>)
        </p>
      </div>

      <div
        className={classNames(
          "text-sm text-red-700 italic",
          (!minSumWeight ||
            pieces!.reduce((r, p) => r + p.weight, 0) >= minSumWeight) &&
            "hidden"
        )}
      >
        *El peso mínimo es {minSumWeight} Kg.
      </div>
    </form>
  );
};

interface PieceFormProps {
  type: ShipmentService;
  initialValues: PieceFormValues;
  pieceCategories: PieceCategoryInterface[];
  pieces?: PieceInterface[];
  applyMinValue?: boolean;
  editMode?: boolean;
  editIndex?: number;
  maxSumWeight?: number;
  maxSumWeightWarn?: number;
  autoCompleteOff?: boolean;
  minSumWeight?: number;
  cleaned?: boolean;
  chargedWeight?: number;
  onSubmit: (prop: PieceInterface, amount: number) => void;
  setEditMode?: (value: boolean) => void;
  setCleaned?: (value: boolean) => void;
  showDimentionsHelp?: boolean;
  setOpenDimentionsHelp?: (open: boolean) => void;
}
const PieceForm: FC<PieceFormProps> = ({
  type,
  initialValues,
  pieceCategories,
  pieces,
  editMode,
  editIndex,
  maxSumWeight = Infinity,
  maxSumWeightWarn = Infinity,
  autoCompleteOff,
  minSumWeight,
  cleaned,
  chargedWeight,
  onSubmit,
  setEditMode,
  setCleaned,
  showDimentionsHelp,
  setOpenDimentionsHelp,
}) => {
  const [weightWarn, setWeightWarn] = useState(false);

  // Validations with Yup for Formik form
  const maxDensity = +(
    useAppSelector((state) => state.inmutable.densityMax?.valueGeneral) ??
    Infinity
  );
  const minVolume = +(
    useAppSelector((state) => state.inmutable.volumeMin?.valueGeneral) ?? 0
  );
  const validationSchema = Yup.object().shape({
    category: Yup.string().required("Este campo es requerido"),
    weight: Yup.number()
      .required("Este campo es requerido")
      .min(0.01, "Debe ser mayor a 0")
      .when("category", {
        is: (val: string) =>
          val === DocumentTypeName && type === ShipmentService.DOCUMENT,
        then: () =>
          Yup.number()
            .required("Este campo es requerido")
            .min(0.01, "Debe ser mayor a 0")
            .max(
              type === ShipmentService.DOCUMENT ? 0.5 : Number.MAX_VALUE,
              "Las piezas no pueden superar los 500 gramos"
            ),
      })
      .test(
        "maxSumWeight",
        `La suma de las piezas no puede superar los ${maxSumWeight} Kg.`,
        function (value) {
          const { amount } = this.parent as PieceFormValues;

          if (editMode && editIndex !== undefined) {
            const { weight: oldWeight } = pieces![editIndex];
            return (
              +(amount ?? 0) * value - oldWeight <=
              maxSumWeight - pieces!.reduce((r, p) => r + p.weight, 0)
            );
          }

          return (
            +(amount ?? 0) * value <=
            maxSumWeight - pieces!.reduce((r, p) => r + p.weight, 0)
          );
        }
      )
      .test("maxSumWeightWarn", "", function (value) {
        const { amount } = this.parent as PieceFormValues;
        let showWarning: boolean;

        if (editMode && editIndex !== undefined) {
          const { weight: oldWeight } = pieces![editIndex];
          showWarning =
            +(amount ?? 0) * value - oldWeight >
            maxSumWeightWarn - pieces!.reduce((r, p) => r + p.weight, 0);
        } else {
          showWarning =
            +(amount ?? 0) * value >
            maxSumWeightWarn - pieces!.reduce((r, p) => r + p.weight, 0);
        }

        if (showWarning) {
          setWeightWarn(true);
        } else {
          setWeightWarn(false);
        }

        return true;
      }),
    height: Yup.number()
      .required("Este campo es requerido")
      .min(0.01, "Debe ser mayor a 0")
      .max(300, "No puede superar los 300 cm")
      .test("minHeightVolume", " ", function (value) {
        const { width, length } = this.parent as PieceFormValues;

        if (!value || !width || !length) return true;

        return (
          (value as number) * (width as number) * (length as number) >=
          minVolume
        );
      })
      .test("maxDensity", " ", function (value) {
        const { weight, width, length } = this.parent as PieceFormValues;

        if (!weight || !width || !length) return true;

        return (
          (weight as number) /
            (value * (width as number) * (length as number)) <=
          maxDensity
        );
      }),
    width: Yup.number()
      .required("Este campo es requerido")
      .min(0.01, "Debe ser mayor a 0")
      .max(300, "No puede superar los 300 cm")
      .test("minWidthVolume", " ", function (value) {
        const { height, length } = this.parent as PieceFormValues;

        if (!value || !height || !length) return true;

        return (
          (value as number) * (height as number) * (length as number) >=
          minVolume
        );
      })
      .test("maxDensity", " ", function (value) {
        const { weight, height, length } = this.parent as PieceFormValues;

        if (!weight || !height || !length) return true;

        return (
          (weight as number) /
            (value * (height as number) * (length as number)) <=
          maxDensity
        );
      }),
    length: Yup.number()
      .required("Este campo es requerido")
      .min(0.01, "Debe ser mayor a 0")
      .max(300, "No puede superar los 300 cm")
      .test("minLengthVolume", " ", function (value) {
        const { height, width } = this.parent as PieceFormValues;

        if (!value || !height || !width) return true;

        return (
          (value as number) * (height as number) * (width as number) >=
          minVolume
        );
      })
      .test("maxDensity", " ", function (value) {
        const { weight, width, height } = this.parent as PieceFormValues;

        if (!weight || !width || !height) return true;

        return (
          (weight as number) /
            (value * (width as number) * (height as number)) <=
          maxDensity
        );
      }),
    amount: Yup.number()
      .required("Este campo es requerido")
      .min(1, "Debe ser mayor a 0")
      .test(
        "maxSumWeight",
        `La suma de las piezas no puede superar los ${maxSumWeight} Kg.`,
        function (value) {
          const { weight } = this.parent as PieceFormValues;

          if (editMode && editIndex !== undefined) {
            const { weight: oldWeight } = pieces![editIndex];
            return (
              value * +(weight ?? 0) - oldWeight <=
              maxSumWeight - pieces!.reduce((r, p) => r + p.weight, 0)
            );
          }

          return (
            value * +(weight ?? 0) <=
            maxSumWeight - pieces!.reduce((r, p) => r + p.weight, 0)
          );
        }
      )
      .test("maxSumWeightWarn", "", function (value) {
        const { weight } = this.parent as PieceFormValues;
        let showWarning: boolean;

        if (editMode && editIndex !== undefined) {
          const { weight: oldWeight } = pieces![editIndex];
          showWarning =
            value * +(weight ?? 0) - oldWeight >
            maxSumWeightWarn - pieces!.reduce((r, p) => r + p.weight, 0);
        } else {
          showWarning =
            value * +(weight ?? 0) >
            maxSumWeightWarn - pieces!.reduce((r, p) => r + p.weight, 0);
        }

        if (showWarning) {
          setWeightWarn(true);
        } else {
          setWeightWarn(false);
        }

        return true;
      }),
  });

  const handleNumberChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    formik: FormikProps<any>,
    allowFloats = true
  ) => {
    if (event.target.value.length > 1) {
      event.target.value = event.target.value.replace(/^0+/, "");
    }
    const setValue = (value: string) => {
      event.target.value = value;
    };

    if (!allowFloats) {
      event.target.value = event.target.value.replace(/[^0-9]/g, "");
    } else if (!formatFloat(event.target.value, setValue)) {
      return;
    }

    formik.handleChange(event);
  };

  const form = useMemo(() => {
    // Create key with all initialValues values
    const initialValuesKey = `${Object.values(initialValues).join("-")}`;

    return (
      <Formik
        key={initialValuesKey}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values, { resetForm }) => {
          onSubmit(
            {
              category: pieceCategories.find(
                (category) => category.name === values.category
              )!,
              value: 0,
              weight: +values.weight,
              height: +values.height,
              width: +values.width,
              length: +values.length,
            },
            +values.amount
          );
          resetForm();
        }}
      >
        {(formik) => (
          <Form
            cleaned={cleaned}
            setCleaned={setCleaned}
            formik={formik}
            type={type}
            pieceCategories={pieceCategories}
            pieces={pieces}
            editMode={editMode}
            weightWarn={weightWarn}
            maxSumWeight={maxSumWeight}
            minSumWeight={minSumWeight}
            showDimentionsHelp={showDimentionsHelp}
            chargedWeight={chargedWeight}
            setEditMode={setEditMode}
            handleNumberChange={handleNumberChange}
            autoCompleteOff={autoCompleteOff}
            setOpenDimentionsHelp={setOpenDimentionsHelp}
          />
        )}
      </Formik>
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    initialValues,
    pieceCategories,
    type,
    editMode,
    onSubmit,
    validationSchema,
  ]);

  return form;
};

export default PieceForm;
